import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Pressable,
  Image,
} from 'react-native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { useAppSelector } from '@/store';
import { useToast } from '@/contexts/admin/ToastContext';
import { useResetPasswordForm } from '@/features/auth/useResetPasswordForm';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { onColor } from '@/constants/shared/theme/onColor';

const SetPasswordScreenNative = require('./SetPasswordScreen.native').SetPasswordScreen;

export const SetPasswordScreen: React.FC = (props) => {
  const { layoutMode } = useResponsive();

  if (layoutMode !== 'desktop') {
    return <SetPasswordScreenNative {...props} />;
  }

  return <SetPasswordScreenDesktop {...props} />;
};

const SetPasswordScreenDesktop: React.FC = () => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const { showSuccess, showError } = useToast();
  const { colors, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;

  const [submitHovered, setSubmitHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);

  const form = useResetPasswordForm(showError, showSuccess);

  const {
    password,
    confirmPassword,
    isLoading,
    formError,
    showPassword,
    showConfirmPassword,
    setPassword,
    setConfirmPassword,
    toggleShowPassword,
    toggleShowConfirmPassword,
    handleSubmit,
    handleCancel,
    passwordRequirements,
  } = form;

  const dynamicStyles = {
    pageWrapper: [
      styles.pageWrapper,
      isAdminMode ? styles.pageWrapperAdmin : styles.pageWrapperResident,
    ],
    splitContainer: [
      styles.splitContainer,
      isMobile && styles.splitContainerMobile,
      isTablet && styles.splitContainerTablet,
      isDesktop && styles.splitContainerDesktop,
    ],
    imagePane: [
      styles.imagePane,
      isMobile && styles.imagePaneMobile,
      isTablet && styles.imagePaneTablet,
      isDesktop && styles.imagePaneDesktop,
    ],
    formPane: [
      styles.formPane,
      isAdminMode ? styles.formPaneAdmin : styles.formPaneResident,
      isMobile && styles.formPaneMobile,
      isTablet && styles.formPaneTablet,
      isDesktop && styles.formPaneDesktop,
    ],
    formInner: [
      styles.formInner,
      isMobile && styles.formInnerMobile,
      isTablet && styles.formInnerTablet,
      isDesktop && styles.formInnerDesktop,
    ],
    title: [
      styles.title,
      isAdminMode ? styles.titleAdmin : styles.titleResident,
      isMobile && styles.titleMobile,
      isTablet && styles.titleTablet,
      isDesktop && styles.titleDesktop,
    ],
  };

  return (
    <View style={dynamicStyles.pageWrapper}>
      <AuthHeader show={true} />

      <View style={dynamicStyles.splitContainer}>
        <View style={dynamicStyles.imagePane}>
          <Image
            source={require('@/assets/images/sign-up.png')}
            style={styles.image}
            resizeMode="cover"
          />
        </View>

        <View style={dynamicStyles.formPane}>
          <View style={dynamicStyles.formInner}>
            <Text style={dynamicStyles.title}>Set your password</Text>

            <Text style={styles.description}>
              Welcome to Resio! Create a password to activate your account.
            </Text>

            <Text style={styles.requirements}>
              Password must be between {passwordRequirements.min} and {passwordRequirements.max} characters.
            </Text>

            {formError && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{formError}</Text>
              </View>
            )}

            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              placeholderTextColor={colors.text.muted}
              value={password}
              onChangeText={setPassword}
              showPassword={showPassword}
              onToggleVisibility={toggleShowPassword}
              editable={!isLoading}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm new password"
              placeholderTextColor={colors.text.muted}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              showPassword={showConfirmPassword}
              onToggleVisibility={toggleShowConfirmPassword}
              editable={!isLoading}
              onSubmitEditing={handleSubmit}
            />

            <View style={styles.buttonContainer}>
              <Pressable
                style={({ pressed }) => [
                  styles.cancelButton,
                  Platform.OS === 'web' && cancelHovered && styles.cancelButtonHovered,
                  Platform.OS === 'web' && pressed && styles.cancelButtonPressed,
                ]}
                onPress={handleCancel}
                disabled={isLoading}
                onHoverIn={Platform.OS === 'web' ? () => setCancelHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setCancelHovered(false) : undefined}
              >
                <Text style={[
                  styles.cancelButtonText,
                  Platform.OS === 'web' && cancelHovered && styles.cancelButtonTextHovered,
                ]}>
                  Cancel
                </Text>
              </Pressable>

              <Pressable
                style={({ pressed }) => [
                  styles.submitButton,
                  isLoading && styles.submitButtonDisabled,
                  Platform.OS === 'web' && submitHovered && !isLoading && styles.submitButtonHovered,
                  Platform.OS === 'web' && pressed && !isLoading && styles.submitButtonPressed,
                ]}
                onPress={handleSubmit}
                disabled={isLoading}
                onHoverIn={Platform.OS === 'web' ? () => setSubmitHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setSubmitHovered(false) : undefined}
              >
                {isLoading ? (
                  <ActivityIndicator color={onColor(semantic.interactive.secondary)} size="small" />
                ) : (
                  <Text style={styles.submitButtonText}>
                    Set Password
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export const SetPasswordDesktopContent: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { colors, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;

  const [submitHovered, setSubmitHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);

  const form = useResetPasswordForm(showError, showSuccess);
  const {
    password, confirmPassword, isLoading, formError,
    showPassword, showConfirmPassword,
    setPassword, setConfirmPassword,
    toggleShowPassword, toggleShowConfirmPassword,
    handleSubmit, handleCancel, passwordRequirements,
  } = form;

  const titleStyle = [styles.title, styles.titleDesktop, isAdminMode ? styles.titleAdmin : styles.titleResident];

  return (
    <View style={desktopContentStyles.formInner}>
      <Text style={titleStyle}>Set your password</Text>

      <Text style={styles.description}>Welcome to Resio! Create a password to activate your account.</Text>

      <Text style={styles.requirements}>
        Password must be between {passwordRequirements.min} and {passwordRequirements.max} characters.
      </Text>

      {formError && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{formError}</Text>
        </View>
      )}

      <PasswordInput
        label="New Password"
        placeholder="Enter new password"
        placeholderTextColor={colors.text.muted}
        value={password}
        onChangeText={setPassword}
        showPassword={showPassword}
        onToggleVisibility={toggleShowPassword}
        editable={!isLoading}
      />

      <PasswordInput
        label="Confirm Password"
        placeholder="Confirm new password"
        placeholderTextColor={colors.text.muted}
        value={confirmPassword}
        onChangeText={setConfirmPassword}
        showPassword={showConfirmPassword}
        onToggleVisibility={toggleShowConfirmPassword}
        editable={!isLoading}
        onSubmitEditing={handleSubmit}
      />

      <View style={styles.buttonContainer}>
        <Pressable
          style={({ pressed }) => [
            styles.cancelButton,
            cancelHovered && styles.cancelButtonHovered,
            pressed && styles.cancelButtonPressed,
          ]}
          onPress={handleCancel}
          disabled={isLoading}
          onHoverIn={() => setCancelHovered(true)}
          onHoverOut={() => setCancelHovered(false)}
        >
          <Text style={[styles.cancelButtonText, cancelHovered && styles.cancelButtonTextHovered]}>Cancel</Text>
        </Pressable>

        <Pressable
          style={({ pressed }) => [
            styles.submitButton,
            isLoading && styles.submitButtonDisabled,
            submitHovered && !isLoading && styles.submitButtonHovered,
            pressed && !isLoading && styles.submitButtonPressed,
          ]}
          onPress={handleSubmit}
          disabled={isLoading}
          onHoverIn={() => setSubmitHovered(true)}
          onHoverOut={() => setSubmitHovered(false)}
        >
          {isLoading ? (
            <ActivityIndicator color={onColor(semantic.interactive.secondary)} size="small" />
          ) : (
            <Text style={styles.submitButtonText}>Set Password</Text>
          )}
        </Pressable>
      </View>
    </View>
  );
};

const desktopContentStyles = StyleSheet.create({
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 40,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
});

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  pageWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && { minHeight: '100vh' as any }),
  },
  pageWrapperAdmin: {
    backgroundColor: colors.bg.base,
  },
  pageWrapperResident: {
    backgroundColor: colors.bg.base,
  },

  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: semantic.border.secondary,
    borderBottomWidth: Platform.select({ web: 1, default: 0 }),
    ...(Platform.OS === 'web' && { maxWidth: 1200 }),
    alignSelf: 'center',
    width: '100%',
  },
  splitContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  splitContainerTablet: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 768,
  },
  splitContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
  },

  imagePane: {
    ...Platform.select({
      web: {
        display: 'flex' as any,
        width: 384,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative' as any,
      },
      default: {
        display: 'none',
      },
    }),
  },
  imagePaneMobile: {
    display: 'none',
  },
  imagePaneTablet: {
    display: 'none',
  },
  imagePaneDesktop: {
    display: 'flex' as any,
    width: 384,
    height: 'calc(100vh - 97px)' as any,
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative' as any,
  },
  image: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && {
      objectFit: 'cover' as any,
      position: 'absolute' as any,
      top: 0,
      left: 0,
    }),
  },

  formPane: {
    width: 512,
    alignItems: 'center',
    justifyContent: Platform.select({ web: 'flex-start', default: 'flex-start' }),
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'auto' as any,
      },
      default: {
        flex: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
      },
    }),
  },
  formPaneAdmin: {
    backgroundColor: colors.bg.base,
  },
  formPaneResident: {
    backgroundColor: colors.bg.base,
  },
  formPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
  },
  formPaneTablet: {
    width: '100%',
    maxWidth: 512,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  formPaneDesktop: {
    width: 512,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    justifyContent: 'flex-start',
    flexShrink: 0,
    overflow: 'auto' as any,
  },

  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingVertical: Platform.select({ web: 40, default: 24 }),
    ...Platform.select({
      web: {
        paddingTop: 40,
        paddingBottom: 40,
        minHeight: '100%' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        justifyContent: 'center' as any,
      },
    }),
  },
  formInnerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    maxWidth: '100%',
  },
  formInnerTablet: {
    paddingHorizontal: 24,
    paddingVertical: 32,
    maxWidth: 480,
  },
  formInnerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 40,
    maxWidth: 512,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },

  title: {
    fontSize: Platform.select({ web: 30, default: 24 }),
    fontWeight: '600',
    textAlign: Platform.select({ web: 'left', default: 'center' }),
    marginBottom: 24,
    marginTop: 24,
  },
  titleAdmin: {
    color: colors.text.primary,
  },
  titleResident: {
    color: colors.text.primary,
  },
  titleMobile: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleTablet: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleDesktop: {
    fontSize: 30,
    textAlign: 'left',
    marginBottom: 24,
  },

  description: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
    marginBottom: 12,
    textAlign: Platform.select({ web: 'left', default: 'center' }),
  },

  requirements: {
    fontSize: 12,
    color: colors.text.muted,
    lineHeight: 18,
    marginBottom: 24,
    textAlign: Platform.select({ web: 'left', default: 'center' }),
  },

  errorContainer: {
    backgroundColor: colors.card.bg,
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  errorText: {
    color: semantic.status.error,
    fontSize: 14,
    lineHeight: 20,
  },

  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 8,
  },
  cancelButton: {
    minWidth: 120,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.text.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
      },
    }),
  },
  cancelButtonHovered: {
    backgroundColor: colors.text.primary,
    borderColor: colors.text.primary,
  },
  cancelButtonPressed: {
    backgroundColor: colors.text.secondary,
    borderColor: colors.text.secondary,
  },
  cancelButtonText: {
    color: colors.text.primary,
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  cancelButtonTextHovered: {
    color: semantic.text.inverse,
  },
  submitButton: {
    minWidth: 120,
    backgroundColor: semantic.interactive.secondary,
    borderWidth: 1,
    borderColor: semantic.interactive.secondary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
      },
    }),
  },
  // Hovered and pressed intentionally carry the same fill as the base button —
  // that is pre-existing behavior, not an oversight of this migration. There is
  // no secondaryHover token to reach for (only primaryHover exists), so giving
  // these states a real shade is a design decision rather than a swap. Left as a
  // faithful no-op so this change stays a pure re-tokenization.
  submitButtonHovered: {
    backgroundColor: semantic.interactive.secondary,
    borderColor: semantic.interactive.secondary,
  },
  submitButtonPressed: {
    backgroundColor: semantic.interactive.secondary,
    borderColor: semantic.interactive.secondary,
  },
  submitButtonDisabled: {
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  submitButtonText: {
    // Derived from the fill rather than restated. The brand coral is the same in
    // both themes, so the old fixed white was under the AA floor in both — this
    // picks the dark ink and roughly doubles the ratio.
    color: onColor(semantic.interactive.secondary),
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
});
